<script setup lang="ts">
import { computed } from 'vue';

import { EntityPresenterMode } from '@satellite/composables/presenterEntityMode';
import { useYardAssetVisitFetcher } from '@satellite/composables/useYardAssetVisitFetcher';
import { useYardAssetVisitDisplay } from '@satellite/composables/useYardAssetVisitDisplay';
import YardAssetVisitDisplay from '@satellite/components/elements/presenters/YardAssetVisitDisplay.vue';

import type { Maybe } from '@nova/core';
import type { PresenterPlaceholderData } from '@satellite/composables/presenterPlaceholder';
import type { YardAssetVisitDisplayField } from '@satellite/composables/useYardAssetVisitDisplay';
import type { YardAssetVisitDetails } from '@satellite/composables/yardAssetVisitPresenterShared';

const {
  size = 'regular',
  orientation = 'horizontal',
  assetVisitId = null,
  initialAssetVisitData = null,
  placeholderAssetVisitData = null,
  staticAssetVisitData = null,
  mode = EntityPresenterMode.Live,
  warehouseTimezone = null,
  displayFields = ['visitType'],
  arrivedTimeProps = {},
  companyNameProps = {},
  companyScacProps = {},
  dockedAtProps = {},
  dotNumberProps = {},
  driverAppointmentIdentifierProps = {},
  driverNotesProps = {},
  licensePlateProps = {},
  phoneProps = {},
  spotProps = {},
  stateIconProps = {},
  truckIconProps = {},
  visitTypeIconProps = {},
  visitTypeProps = {}
} = defineProps<{
  size?: 'regular' | 'small' | 'large';
  orientation?: 'horizontal' | 'vertical';
  assetVisitId?: Maybe<string>;
  initialAssetVisitData?: Maybe<YardAssetVisitDetails>;
  placeholderAssetVisitData?: Maybe<PresenterPlaceholderData<YardAssetVisitDetails>>;
  staticAssetVisitData?: Maybe<YardAssetVisitDetails>;
  mode?: EntityPresenterMode;
  displayFields?: YardAssetVisitDisplayField[];
  warehouseTimezone?: Maybe<string>;
  arrivedTimeProps?: Record<string, unknown>;
  companyNameProps?: Record<string, unknown>;
  companyScacProps?: Record<string, unknown>;
  dockedAtProps?: Record<string, unknown>;
  dotNumberProps?: Record<string, unknown>;
  driverAppointmentIdentifierProps?: Record<string, unknown>;
  driverNotesProps?: Record<string, unknown>;
  licensePlateProps?: Record<string, unknown>;
  phoneProps?: Record<string, unknown>;
  spotProps?: Record<string, unknown>;
  stateIconProps?: Record<string, unknown>;
  truckIconProps?: Record<string, unknown>;
  visitTypeIconProps?: Record<string, unknown>;
  visitTypeProps?: Record<string, unknown>;
}>();

const {
  data: visit,
  isLoading,
  isFetching,
  onIntersect
} = useYardAssetVisitFetcher({
  assetVisitId: computed(() => assetVisitId),
  initialAssetVisitData: computed(() => initialAssetVisitData),
  placeholderAssetVisitData: computed(() => placeholderAssetVisitData),
  staticAssetVisitData: computed(() => staticAssetVisitData),
  mode
});

const data = useYardAssetVisitDisplay(visit, {
  size: computed(() => size),
  warehouseTimezone: computed(() => warehouseTimezone),
  mode: computed(() => mode),
  isFetching,
  arrivedTimeProps: computed(() => arrivedTimeProps),
  companyNameProps: computed(() => companyNameProps),
  companyScacProps: computed(() => companyScacProps),
  dockedAtProps: computed(() => dockedAtProps),
  dotNumberProps: computed(() => dotNumberProps),
  driverAppointmentIdentifierProps: computed(() => driverAppointmentIdentifierProps),
  driverNotesProps: computed(() => driverNotesProps),
  licensePlateProps: computed(() => licensePlateProps),
  phoneProps: computed(() => phoneProps),
  spotPresenterProps: computed(() => spotProps),
  stateIconProps: computed(() => stateIconProps),
  truckIconProps: computed(() => truckIconProps),
  visitTypeIconProps: computed(() => visitTypeIconProps),
  visitTypeProps: computed(() => visitTypeProps)
});
</script>

<template>
  <v-skeleton-loader
    :loading="isLoading"
    type="text"
    v-intersect="{
      handler: onIntersect,
      options: {
        threshold: [0.5]
      }
    }"
    class="asset-visit-presenter-v2 bg-transparent">
    <yard-asset-visit-display
      :data="data"
      :display-fields="displayFields"
      :orientation="orientation"
      :style="isLoading ? 'min-width: 50px' : ''">
      <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
        <slot :name="slotName" v-bind="slotProps ?? {}" />
      </template>
    </yard-asset-visit-display>
  </v-skeleton-loader>
</template>

<style scoped>
:deep(.v-skeleton-loader__text) {
  margin: 0 auto !important;
  height: 1em !important;
}
</style>
